import { usePrevious } from '@lemonade-hq/cdk';
import type { Milliseconds } from '@lemonade-hq/ts-helpers';
import type { UniclientAction, UniclientFrame } from '@lemonade-hq/uniclient-chat';
import { Flex, spacing, useDelayedValue } from '@lemonade-hq/uniclient-theme';
import { AnimatePresence, motion } from 'motion/react';
import React, { useMemo } from 'react';
import { WIZARD_PROGRESS_ELEMENT_ID } from '../utils/consts';
import { itemContainerVariants } from './WizardProgress/animations';
import { ProgressItem } from './WizardProgress/components/ProgressItem';
import { LEVEL_ONE_GAP } from './WizardProgress/constants';
import { useCurrentIndex } from './WizardProgress/hooks';
import { parseItemId } from './WizardProgress/utils';
import * as styles from './WizardProgress.css';

type WizardProgressProps = NonNullable<UniclientFrame['progress']> & {
  readonly onAction?: (action: UniclientAction) => Promise<unknown>;
};

const SHORT_TRANSITION_DELAY: Milliseconds = 300;

export const WizardProgress: React.FC<WizardProgressProps> = ({ items, currentItemId, onAction }) => {
  const { itemId, subItemId, subSubItemId } = useMemo(() => parseItemId(currentItemId), [currentItemId]);
  const currentIndex = useCurrentIndex(itemId, items);

  const delayedCurrent = useDelayedValue(
    currentIndex,
    currentIndex,
    currentIndex > 0 && (items[currentIndex - 1].subItems?.length ?? 0) > 0 ? SHORT_TRANSITION_DELAY : 0,
  );
  const isFirstRender = usePrevious(false, true);

  const itemsKey = useMemo(
    () =>
      items
        .map(item => {
          const subItemsKey =
            item.subItems
              ?.map(sub => {
                const subSubItemsKey = sub.subItems?.map(subSub => subSub.id).join(',') ?? '';
                return `${sub.id}(${subSubItemsKey})`;
              })
              .join('.') ?? '';
          return `${item.id}:${subItemsKey}`;
        })
        .join('-'),
    [items],
  );

  return (
    <Flex
      className={styles.progress}
      flexDirection="column"
      gap={LEVEL_ONE_GAP}
      id={WIZARD_PROGRESS_ELEMENT_ID}
      paddingBlockEnd={{ sm: spacing.s08, lg: 0 }}
      paddingBlockStart={{ sm: spacing.s16, lg: 0 }}
      paddingInlineStart={{ sm: spacing.s16, lg: 0 }}
      role="list"
      width="100%"
    >
      <AnimatePresence mode="wait">
        <motion.div
          animate="animate"
          exit="exit"
          initial="initial"
          key={itemsKey}
          style={{ display: 'flex', flexDirection: 'column', gap: LEVEL_ONE_GAP, width: '100%' }}
          variants={itemContainerVariants}
        >
          {items.map((item, index) => {
            const status = item.status ?? (index < currentIndex ? 'completed' : 'pending');
            const current = index === delayedCurrent;
            const completed = (!current && status !== 'pending') || status === 'completed';

            return (
              <ProgressItem
                completed={completed}
                current={current}
                currentIndex={currentIndex}
                currentSubItemId={subItemId}
                currentSubSubItemId={subSubItemId}
                isFirstRender={isFirstRender}
                isLast={index === items.length - 1}
                item={item}
                key={item.id}
                onAction={item.action ? onAction : undefined}
                status={status}
              />
            );
          })}
        </motion.div>
      </AnimatePresence>
    </Flex>
  );
};
